import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, fullUrl } from "../../lib/api";
import { LogOut, Phone, Mail, RefreshCcw, X } from "lucide-react";

const STATUSES = [
  { key: "new", label: "New", color: "bg-[#C5A059]" },
  { key: "contacted", label: "Contacted", color: "bg-[#3B82F6]" },
  { key: "quoted", label: "Quoted", color: "bg-[#8B5CF6]" },
  { key: "won", label: "Won", color: "bg-[#16A34A]" },
  { key: "lost", label: "Lost", color: "bg-[#94A3B8]" },
];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, quoted: 0, won: 0, lost: 0 });
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!localStorage.getItem("cps_token")) {
      nav("/admin/login");
      return;
    }
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: ls }, { data: s }] = await Promise.all([
        api.get("/leads"),
        api.get("/leads-stats"),
      ]);
      setLeads(ls);
      setStats(s);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("cps_token");
        nav("/admin/login");
      } else {
        toast.error("Failed to load leads");
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("cps_token");
    localStorage.removeItem("cps_email");
    nav("/admin/login");
  };

  const filtered = useMemo(() => filter === "all" ? leads : leads.filter((l) => l.status === filter), [leads, filter]);

  const updateStatus = async (lead, status) => {
    try {
      const { data } = await api.patch(`/leads/${lead.id}`, { status });
      setLeads((prev) => prev.map((l) => l.id === lead.id ? data : l));
      if (selected?.id === lead.id) setSelected(data);
      toast.success(`Marked as ${status}`);
      load();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const updateNotes = async (lead, notes) => {
    try {
      const { data } = await api.patch(`/leads/${lead.id}`, { notes });
      setLeads((prev) => prev.map((l) => l.id === lead.id ? data : l));
      setSelected(data);
    } catch {
      toast.error("Failed to save notes");
    }
  };

  const deleteLead = async (lead) => {
    if (!window.confirm(`Delete lead from ${lead.name}?`)) return;
    try {
      await api.delete(`/leads/${lead.id}`);
      setLeads((prev) => prev.filter((l) => l.id !== lead.id));
      if (selected?.id === lead.id) setSelected(null);
      toast.success("Lead deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="page-admin-dashboard">
      <header className="bg-[#0B1426] text-white">
        <div className="container-cps flex items-center justify-between h-16">
          <Link to="/" className="font-display text-xl">Connolly's<span style={{ color: "#C5A059" }}>.</span> <span className="text-xs uppercase tracking-wider text-white/60 ml-2">Admin</span></Link>
          <div className="flex items-center gap-4">
            <button onClick={load} className="text-white/80 hover:text-white" aria-label="Refresh" data-testid="admin-refresh"><RefreshCcw size={16} /></button>
            <button onClick={logout} className="flex items-center gap-2 text-sm text-white/80 hover:text-white" data-testid="admin-logout">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="container-cps py-10">
        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-1 mb-8" data-testid="admin-stats">
          <Stat label="Total" value={stats.total} testid="stat-total" />
          {STATUSES.map((s) => (
            <Stat key={s.key} label={s.label} value={stats[s.key] || 0} dot={s.color} testid={`stat-${s.key}`} />
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          <FilterBtn active={filter === "all"} onClick={() => setFilter("all")} testid="filter-all">All ({leads.length})</FilterBtn>
          {STATUSES.map((s) => (
            <FilterBtn key={s.key} active={filter === s.key} onClick={() => setFilter(s.key)} dot={s.color} testid={`filter-${s.key}`}>
              {s.label} ({stats[s.key] || 0})
            </FilterBtn>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 bg-white border border-[#E5E3DB]">
            <table className="w-full text-sm" data-testid="admin-leads-table">
              <thead className="bg-[#F9F8F6] text-left">
                <tr className="border-b border-[#E5E3DB]">
                  <th className="px-4 py-3 text-xs uppercase tracking-wider text-[#4F5B6A]">Lead</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wider text-[#4F5B6A]">Suburb</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wider text-[#4F5B6A]">Service</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wider text-[#4F5B6A]">Status</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wider text-[#4F5B6A]">When</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="px-4 py-12 text-center text-[#4F5B6A]">Loading…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-12 text-center text-[#4F5B6A]">No leads yet.</td></tr>
                ) : filtered.map((l) => {
                  const st = STATUSES.find((s) => s.key === l.status) || STATUSES[0];
                  return (
                    <tr key={l.id} className={`border-b border-[#E5E3DB] cursor-pointer ${selected?.id === l.id ? "bg-[#F9F8F6]" : "hover:bg-[#F9F8F6]"}`} onClick={() => setSelected(l)} data-testid={`lead-row-${l.id}`}>
                      <td className="px-4 py-4">
                        <div className="font-medium text-[#0B1426]">{l.name}</div>
                        <div className="text-xs text-[#4F5B6A]">{l.phone}</div>
                      </td>
                      <td className="px-4 py-4 text-[#0B1426]">{l.suburb || "—"}</td>
                      <td className="px-4 py-4 text-[#0B1426]">{l.service || "—"}</td>
                      <td className="px-4 py-4">
                        <span className="inline-flex items-center gap-2 text-xs">
                          <span className={`w-2 h-2 rounded-full ${st.color}`} /> {st.label}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-xs text-[#4F5B6A]">{new Date(l.created_at).toLocaleDateString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="lg:col-span-5">
            {selected ? (
              <LeadDetail
                lead={selected}
                onStatus={(s) => updateStatus(selected, s)}
                onNotes={(n) => updateNotes(selected, n)}
                onClose={() => setSelected(null)}
                onDelete={() => deleteLead(selected)}
              />
            ) : (
              <div className="bg-white border border-[#E5E3DB] p-8 text-[#4F5B6A]" data-testid="admin-empty-detail">
                Select a lead from the table to view details.
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value, dot, testid }) {
  return (
    <div className="bg-white p-5 border border-[#E5E3DB]" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#4F5B6A]">
        {dot && <span className={`w-2 h-2 rounded-full ${dot}`} />} {label}
      </div>
      <div className="font-display text-3xl text-[#0B1426] mt-2">{value}</div>
    </div>
  );
}

function FilterBtn({ children, active, onClick, dot, testid }) {
  return (
    <button onClick={onClick} className={`px-4 py-2 text-xs uppercase tracking-wider border flex items-center gap-2 ${active ? "bg-[#0B1426] text-white border-[#0B1426]" : "bg-white text-[#0B1426] border-[#E5E3DB] hover:border-[#0B1426]"}`} data-testid={testid}>
      {dot && <span className={`w-2 h-2 rounded-full ${dot}`} />} {children}
    </button>
  );
}

function LeadDetail({ lead, onStatus, onNotes, onClose, onDelete }) {
  const [notes, setNotes] = useState(lead.notes || "");
  useEffect(() => setNotes(lead.notes || ""), [lead.id]);
  return (
    <div className="bg-white border border-[#E5E3DB] p-6 md:p-8 sticky top-6" data-testid="admin-lead-detail">
      <div className="flex items-start justify-between">
        <div>
          <div className="overline">Lead</div>
          <h2 className="font-display text-2xl text-[#0B1426] mt-2">{lead.name}</h2>
        </div>
        <button onClick={onClose} aria-label="Close" className="text-[#4F5B6A] hover:text-[#0B1426]"><X size={16} /></button>
      </div>

      <div className="mt-6 space-y-3 text-sm">
        <a href={`tel:${lead.phone}`} className="flex items-center gap-3 text-[#0B1426] hover:text-[#C5A059]" data-testid="detail-phone"><Phone size={14} /> {lead.phone}</a>
        <a href={`mailto:${lead.email}`} className="flex items-center gap-3 text-[#0B1426] hover:text-[#C5A059]" data-testid="detail-email"><Mail size={14} /> {lead.email}</a>
        <div className="grid grid-cols-2 gap-3 text-xs text-[#4F5B6A] mt-3">
          <div><div className="uppercase tracking-wider">Suburb</div><div className="text-[#0B1426] text-sm mt-0.5">{lead.suburb || "—"}</div></div>
          <div><div className="uppercase tracking-wider">Service</div><div className="text-[#0B1426] text-sm mt-0.5">{lead.service || "—"}</div></div>
          <div><div className="uppercase tracking-wider">Property</div><div className="text-[#0B1426] text-sm mt-0.5">{lead.property_type || "—"}</div></div>
          <div><div className="uppercase tracking-wider">Callback</div><div className="text-[#0B1426] text-sm mt-0.5">{lead.request_callback ? "Yes" : "No"}</div></div>
          <div className="col-span-2"><div className="uppercase tracking-wider">Source</div><div className="text-[#0B1426] text-sm mt-0.5">{lead.source_page || "—"}</div></div>
        </div>
        {lead.message && (
          <div className="mt-4 border-l-2 border-[#C5A059] pl-4 text-[#0B1426]">
            <div className="text-xs uppercase tracking-wider text-[#4F5B6A] mb-1">Message</div>
            {lead.message}
          </div>
        )}
        {lead.photo_urls?.length > 0 && (
          <div>
            <div className="text-xs uppercase tracking-wider text-[#4F5B6A] mb-2 mt-4">Photos</div>
            <div className="flex flex-wrap gap-2">
              {lead.photo_urls.map((u, i) => (
                <a key={i} href={fullUrl(u)} target="_blank" rel="noreferrer" className="block">
                  <img src={fullUrl(u)} alt={`photo ${i+1}`} className="w-20 h-20 object-cover border border-[#E5E3DB]" />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-6">
        <div className="text-xs uppercase tracking-wider text-[#4F5B6A] mb-2">Status</div>
        <div className="flex flex-wrap gap-2">
          {STATUSES.map((s) => (
            <button key={s.key} onClick={() => onStatus(s.key)} className={`px-3 py-2 text-xs uppercase tracking-wider border flex items-center gap-2 ${lead.status === s.key ? "bg-[#0B1426] text-white border-[#0B1426]" : "bg-white text-[#0B1426] border-[#E5E3DB] hover:border-[#0B1426]"}`} data-testid={`status-btn-${s.key}`}>
              <span className={`w-2 h-2 rounded-full ${s.color}`} /> {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6">
        <div className="text-xs uppercase tracking-wider text-[#4F5B6A] mb-2">Internal notes</div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() => onNotes(notes)}
          rows={4}
          className="w-full border border-[#E5E3DB] p-3 text-sm focus:outline-none focus:border-[#C5A059]"
          placeholder="Quote sent, called back, follow-up next week…"
          data-testid="detail-notes"
        />
      </div>

      <div className="mt-6 flex justify-between">
        <button onClick={onDelete} className="text-xs uppercase tracking-wider text-[#4F5B6A] hover:text-red-600" data-testid="detail-delete">Delete lead</button>
        <span className="text-xs text-[#4F5B6A]">{new Date(lead.created_at).toLocaleString()}</span>
      </div>
    </div>
  );
}
